import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useGetLatestBillOfMaterialSummary } from "../../../../api/billOfMaterialSummary";
import { useGetSalesUnitByIngredient } from "../../../../api/ingredients";
import { Card, CardContent, CardHeader } from "../../../../components/ui/common/card";
import { Form } from "../../../../components/ui/common/form";
import { Skeleton } from "../../../../components/ui/common/skeleton";
import { FormScientificUnitInput } from "../../../../components/ui/Input/FormScientificUnitInput/FormScientificUnitInput";
import { LcalCulculationDefultValues } from "../../../../config/constants";
import { DocumentItem } from "../../../ingredients/shared/components/ImpactResultDocuments/hooks/useDocumentManagement";
import { ImpactResultDocuments } from "../../../ingredients/shared/components/ImpactResultDocuments/ImpactResultDocuments";
import { SupplierIngredientLcaDataViewRef } from "../../../ingredients/shared/components/SupplierIngredientLcaDataView";

interface LcaDataSectionProps {
  dataView?: boolean;
  ingredientId: string;
  supplierId: string;
}

const formatCategoryLabel = (key: string) =>
  key
    .split("_")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

/**
 * "Impact results" card — Tailwind rebuild of the measure/unit grid inside
 * the legacy `SupplierIngredientLcaDataView`
 * (src/screens/ingredients/shared/components/SupplierIngredientLcaDataView.tsx),
 * which built each field from the Semantic UI `InputWithDropDown`. Reuses
 * that component's `ImpactResultDocuments` section as-is (already Tailwind,
 * no Semantic UI) and implements the exact same
 * `SupplierIngredientLcaDataViewRef` imperative-handle contract so
 * `useIngredientRegisterViewModel`'s `lcaRef`/`useSupplierSave` (one of the
 * 6 protected legacy hooks) work against it unchanged.
 *
 * The legacy field's "unit" was always a single-option, effectively
 * read-only dropdown (`options={[{value: unit}]}`) — here it's
 * `FormScientificUnitInput`'s static `unitType` display (a merged input+unit
 * box, no `unitOptions`/`unitName`) instead of a fake select. Impact values
 * are frequently entered/copied in scientific notation (e.g. `2.5e-6`), so
 * this section uses `FormScientificUnitInput` rather than the plain
 * `FormUnitInput` used elsewhere. The unit value itself doesn't need a
 * registered field to be saved: `useForm`'s `values` option keeps the form's
 * internal state (which `getValues()` reads from) in sync with the full
 * `{measure, unit}` shape for every category regardless of which fields are
 * actually rendered/registered.
 */
const LcaDataSection = forwardRef<SupplierIngredientLcaDataViewRef, LcaDataSectionProps>(
  ({ dataView = false, ingredientId, supplierId }, ref) => {
    const [uploadedDocuments, setUploadedDocuments] = useState<DocumentItem[]>([]);
    const documentsRef = useRef<{
      getDocuments(): DocumentItem[];
      getUploadedDocuments(): unknown[];
    }>(null);

    const { data: latestBillOfMaterialSummary, isFetching: isLatestSummaryFetching } =
      useGetLatestBillOfMaterialSummary({ ingredientId, supplierId });

    const { data: ingredientDetailData } = useGetSalesUnitByIngredient({
      ingredientId,
      supplierId
    });

    useEffect(() => {
      if (
        ingredientDetailData?.supportingDocuments &&
        Array.isArray(ingredientDetailData.supportingDocuments)
      ) {
        const existingDocs = ingredientDetailData.supportingDocuments.map(
          (doc: any, index: number) => ({
            id: doc._id || `existing-${index}`,
            file: new File([], doc.fileName || `Document ${index + 1}`),
            description: doc.description || "",
            note: doc.note || "",
            uploadedAt: new Date(doc.uploadedAt || Date.now()),
            uploadedResponse: doc,
            isUploading: false
          })
        );
        setUploadedDocuments(existingDocs);
      } else if (ingredientDetailData && !ingredientDetailData.supportingDocuments) {
        setUploadedDocuments([]);
      }
    }, [ingredientDetailData]);

    const values = useMemo(() => {
      return Object.keys(LcalCulculationDefultValues).reduce((acc, key) => {
        const defaultValue =
          LcalCulculationDefultValues[key as keyof typeof LcalCulculationDefultValues];
        acc[key] = {
          measure: (latestBillOfMaterialSummary?.totalImpact as any)?.[key]?.measure || null,
          unit: defaultValue.unit
        };
        return acc;
      }, {} as Record<string, { measure: number | null; unit: string }>);
    }, [latestBillOfMaterialSummary]);

    const methods = useForm({ mode: "all", values, defaultValues: values });
    const { control, getValues } = methods;

    useImperativeHandle(ref, () => ({
      supplierLCAData: () => {
        const rawValues = getValues() as Record<string, { measure?: unknown; unit?: string }>;
        return Object.entries(LcalCulculationDefultValues).reduce((acc, [key, defaultVal]) => {
          const input = rawValues[key];
          acc[key] = { measure: Number(input?.measure), unit: input?.unit || defaultVal.unit };
          return acc;
        }, {} as Record<string, { measure: number; unit: string }>);
      },
      getDocuments: () => documentsRef.current?.getDocuments() || [],
      getUploadedDocuments: () => documentsRef.current?.getUploadedDocuments() || []
    }));

    return (
      <Form {...methods}>
        <Card className="tw-gap-3 tw-py-4">
          <CardHeader className="tw-px-4">
            <h3 className="tw-text-base tw-font-semibold tw-text-foreground">Impact results</h3>
            <p className="tw-text-sm tw-text-muted-foreground">
              Enter the calculated LCA results per kg of ingredient (EF 3.1 categories).
            </p>
          </CardHeader>
          <CardContent className="tw-px-4">
            {isLatestSummaryFetching ? (
              <div className="tw-grid tw-grid-cols-2 tw-gap-3 md:tw-grid-cols-4">
                {[...Array(16)].map((_, index) => (
                  <Skeleton key={index} className="tw-h-16 tw-w-full" />
                ))}
              </div>
            ) : (
              <div className="tw-grid tw-grid-cols-2 tw-gap-3 md:tw-grid-cols-4">
                {Object.keys(LcalCulculationDefultValues).map(key => {
                  const { unit } =
                    LcalCulculationDefultValues[key as keyof typeof LcalCulculationDefultValues];
                  return (
                    <FormScientificUnitInput
                      key={key}
                      control={control}
                      name={`${key}.measure`}
                      label={formatCategoryLabel(key)}
                      placeholder="0.00"
                      disabled={dataView}
                      unitType={unit}
                    />
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Read-only ("view ingredient") callers render their own
            `ImpactResultDocumentsReadOnly` card instead (see
            `ingredientDetails/components/tabs/SummaryTab.tsx`) - this
            edit-capable section only makes sense in the register/edit forms,
            which never set `dataView`. */}
        {!dataView && (
          <ImpactResultDocuments
            ref={documentsRef}
            control={control}
            dataView={dataView}
            documents={uploadedDocuments}
            onDocumentsChange={setUploadedDocuments}
            ingredientId={ingredientId}
            supplierId={supplierId}
          />
        )}
      </Form>
    );
  }
);

LcaDataSection.displayName = "LcaDataSection";

export default LcaDataSection;
